import React, { useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useCarousel } from '../../hooks/useCarousel';
import { CarouselImage } from '../../types';
import './Carousel.css';

interface CarouselProps {
  images: CarouselImage[];
  autoplay?: boolean;
  interval?: number;
  pauseOnHover?: boolean;
  showControls?: boolean;
  showIndicators?: boolean;
  className?: string;
  /** Etiqueta accesible de la región del carrusel. Si se pasa, el contenedor
   *  expone `role="region"` + `aria-roledescription="carrusel"`. */
  ariaLabel?: string;
  /** Dimensiones intrínsecas de las imágenes (previenen layout shift).
   *  Default 444×250 (uso original en "Quiénes Somos"). */
  imgWidth?: number;
  imgHeight?: number;
  /** `fetchpriority` para la PRIMERA imagen (candidata a LCP si el carrusel
   *  está above-the-fold, como en el hero). Omitir en carruseles bajo el fold. */
  imgFetchPriority?: 'high' | 'low' | 'auto';
}

/**
 * Componente Carousel - Carrusel de imágenes reutilizable
 * Migrado del JavaScript original con todas las funcionalidades
 */
const Carousel: React.FC<CarouselProps> = ({
  images,
  autoplay = true,
  interval = 5000,
  pauseOnHover = true,
  showControls = true,
  showIndicators = true,
  className = '',
  ariaLabel,
  imgWidth = 444,
  imgHeight = 250,
  imgFetchPriority
}) => {
  const [touchStartX, setTouchStartX] = useState(0);

  // Respeta prefers-reduced-motion: sin auto-rotación (el usuario avanza con
  // los indicadores). framer-motion ya es dependencia del proyecto.
  const prefersReducedMotion = useReducedMotion();
  const effectiveAutoplay = autoplay && !prefersReducedMotion;

  const {
    currentIndex,
    goToSlide,
    nextSlide,
    prevSlide,
    handleMouseEnter,
    handleMouseLeave,
    handleTouchStart,
    handleTouchEnd
  } = useCarousel(images, effectiveAutoplay, interval, pauseOnHover);

  // Manejar inicio de toque
  const onTouchStart = (e: React.TouchEvent) => {
    const startX = handleTouchStart(e);
    setTouchStartX(startX);
  };

  // Manejar fin de toque
  const onTouchEnd = (e: React.TouchEvent) => {
    handleTouchEnd(touchStartX, e);
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div
      className={`carousel-container ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role={ariaLabel ? 'region' : undefined}
      aria-roledescription={ariaLabel ? 'carrusel' : undefined}
      aria-label={ariaLabel}
    >
      {/* Slides del carrusel */}
      <div className="carousel-slides">
        {images.map((image, index) => (
          <div
            key={index}
            className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
            data-index={index}
            aria-hidden={index !== currentIndex}
          >
            {image.srcMobile ? (
              <picture>
                <source media="(max-width: 768px)" srcSet={image.srcMobile} />
                <img
                  src={image.src}
                  alt={image.alt}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? imgFetchPriority : undefined}
                  width={imgWidth}
                  height={imgHeight}
                  onLoad={(e) => {
                    (e.target as HTMLImageElement).classList.add('loaded');
                  }}
                />
              </picture>
            ) : (
              <img
                src={image.src}
                alt={image.alt}
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? imgFetchPriority : undefined}
                width={imgWidth}
                height={imgHeight}
                srcSet={image.srcset}
                sizes={image.srcset ? '(max-width: 600px) 444px, 740px' : undefined}
                onLoad={(e) => {
                  (e.target as HTMLImageElement).classList.add('loaded');
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Controles de navegación */}
      {showControls && images.length > 1 && (
        <>
          <button
            className="carousel-control prev"
            onClick={prevSlide}
            aria-label="Imagen anterior"
            type="button"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <button
            className="carousel-control next"
            onClick={nextSlide}
            aria-label="Imagen siguiente"
            type="button"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </>
      )}

      {/* Indicadores */}
      {showIndicators && images.length > 1 && (
        <div className="carousel-indicators">
          {images.map((_, index) => (
            <button
              key={index}
              className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Ir a la imagen ${index + 1} de ${images.length}`}
              aria-current={index === currentIndex}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;